import PropTypes from 'prop-types';
import { Link } from 'react-router';

import { RETURN_REASONS } from 'constants/returnReasons';

import { Loader } from 'components/Loader';
import ReturnsUtils from 'helpers/ReturnsUtils';
import FinalSale from 'zen/components/common/FinalSale';
import ProductImage from 'components/cart/ProductImage';
import css from 'styles/containers/returns/returnProductCards.scss';

const makeReturnReasons = (unitId, boxNum, onSelectReturnReason) => (
  <>
    <label htmlFor="reasons">Why are you returning this?</label>
    <select
      id="reasons"
      data-unitid={unitId}
      data-boxnum={boxNum}
      onChange={onSelectReturnReason}>
      {RETURN_REASONS.map(reason => {
        const { text, reasonCode } = reason;
        return <option key={reasonCode} value={reasonCode}>{text}</option>;
      })}
    </select>
  </>
);

const makeBoxDropdown = (boxNum, itemUniqueId, onProductBoxNumSelect) => {
  const options = [];

  for (let i = 1; i < (boxNum + 1); i++) {
    options.push(<option key={i} value={`${i}-${itemUniqueId}`}>Box {i}</option>);
  }

  return (
    <div className={css.boxSelect}>
      <label htmlFor="boxSelection">I will be placing this item in box</label>
      <select onChange={onProductBoxNumSelect}>
        {options}
      </select>
    </div>
  );
};

// when items come from orders state
const getItemProperties = product => {
  const { asin, name, attributes } = product;
  const color = attributes[1]?.value;
  const size = attributes[2]?.value;
  const width = attributes[3]?.value;

  return {
    asin,
    title: name,
    color,
    size,
    width
  };
};

const makeHeading = (orderDate, orderId, step, boxNum, singleBox) => {
  // render what box each item is in
  if (step === 'SELECT_LABEL' || step === 'DONE') {
    return (
      <div className={css.heading}>
        Items in Box {!singleBox ? boxNum : ''}
      </div>
    );
  }

  // render days remaining and order number
  if (step === 'SELECT_RETURN_ITEMS') {
    const daysRemaining = orderDate && ReturnsUtils.getDaysRemaining(orderDate);
    const formattedDate = orderDate?.split(' at ')[0];

    return (
      <>
      <div className={css.heading}>
        <div>Days Remaining <span>{daysRemaining}</span></div>
        <div>Order Date <span>{formattedDate}</span></div>
        <div>Order Number <span>{orderId}</span></div>
      </div>

      <div className={css.legend}>
        <p>Return</p>
        <p>Item(s)</p>
      </div>
    </>
    );
  }

  // dont render anything (select_box step or anything else)
  return null;
};

const ReturnProductCards = ({
  items,
  returnItems,
  orderDate,
  step,
  orderId,
  boxNum,
  onReturnChecked,
  onProductBoxNumSelect,
  singleBox,
  onSelectReturnReason
}, { marketplace: { account: { multiBoxReturns } } }) => {

  if (!items) {
    return <Loader />;
  }

  return (
    <div className={css.container}>
      { makeHeading(orderDate, orderId, step, boxNum, singleBox) }

      {items.map(item => {
        const { image, product, returnable, itemUniqueId, unitId } = item;

        if (!returnable) {
          return null;
        }

        const { asin, title, color, size, width, finalSale } = (product ? getItemProperties(product) : item);
        const url = image?.href || product.productUrl;

        const isChecked = returnItems?.find(returnItem => returnItem.itemUniqueId === itemUniqueId);
        const ariaCardId = `aria-returnProductCard-${itemUniqueId || unitId}`;

        return (
          <div key={itemUniqueId || unitId} className={css.item}>

            { step === 'SELECT_RETURN_ITEMS' &&
              <div>
                <input
                  id={`item-${itemUniqueId}`}
                  defaultChecked={isChecked}
                  className={css.check}
                  onChange={onReturnChecked.bind(this, { item, orderId })}
                  type="checkbox"
                  aria-describedby={ariaCardId} />
                <label htmlFor={`item-${itemUniqueId}`} aria-label="return" />
              </div>
            }

            <ProductImage
              className={css.image}
              item={product || item}
              dataTe="TE_RETURNS_PRODUCT_CLICK" />

            <div className={css.info}>
              {(step === 'SELECT_BOX' && multiBoxReturns) && makeBoxDropdown(boxNum, itemUniqueId, onProductBoxNumSelect)}

              <div id={ariaCardId} className={css.description}>
                <Link to={url}>{title}</Link>
                <p>Asin: {asin}</p>
                <p>Color: {color}</p>
                { size && <p>Size: {size}</p> }
                { width && <p>Width: {width}</p> }
                { finalSale && <FinalSale /> }
              </div>
              {step === 'SELECT_LABEL' && makeReturnReasons(unitId, boxNum, onSelectReturnReason)}
            </div>
          </div>
        );
      })}
    </div>
  );
};

ReturnProductCards.contextTypes = {
  marketplace: PropTypes.object
};

export default ReturnProductCards;
